// React/finance-app/src/SendMessages.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { fetchGatewayListFromDB } from './api';
import { BatchActivationBadge, isServiceNumber } from './shared/portActivation';

const SendSMSForm = () => {
  const [mode, setMode] = useState('simple'); // 'simple' or 'advanced'
  const [formData, setFormData] = useState({
    recipient: '',
    message: '',
    port: '',
    gateway_id: '',
  });
  const [batchFormData, setBatchFormData] = useState({
    batch_name: '',
    gateway_id: '',
    mode: 'standard', // 'standard', 'port_to_port', 'random_sender'
    sender_ports: '',
    receiver_ports: '', // For port-to-port mode
    receiver_gateway_id: '', // For port-to-port mode: gateway for receiver ports
    recipients: '',
    message_pool: '',
    tracking_port_id: '',
    delay_between_sends_seconds: 0,
    wait_for_activation_timeout_seconds: 300,
  });
  const [gateways, setGateways] = useState([]);
  const [ports, setPorts] = useState([]);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [debug, setDebug] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [eventLog, setEventLog] = useState([]);
  const [previousStatus, setPreviousStatus] = useState(null);
  const [batchHistory, setBatchHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedSenderPorts, setSelectedSenderPorts] = useState([]);
  const [selectedReceiverPorts, setSelectedReceiverPorts] = useState([]);
  const [showPortSelector, setShowPortSelector] = useState(false);
  const [selectedGatewayIds, setSelectedGatewayIds] = useState([]);
  const [showGatewaySelector, setShowGatewaySelector] = useState(false);
  const [showReceiverPortSelector, setShowReceiverPortSelector] = useState(false);
  const [receiverPorts, setReceiverPorts] = useState([]);
  const pollingIntervalRef = useRef(null);
  const [filterDaysMin, setFilterDaysMin] = useState(79);
  const [filterDaysMax, setFilterDaysMax] = useState(82);
  const [filteringPorts, setFilteringPorts] = useState(false);
  const [recipientGatewayId, setRecipientGatewayId] = useState('');
  const [loadingRecipientPorts, setLoadingRecipientPorts] = useState(false);
  const [loadingSenderAssignedPorts, setLoadingSenderAssignedPorts] = useState(false);
  const [showDeadPorts, setShowDeadPorts] = useState(false);
  const [activateBeforeSend, setActivateBeforeSend] = useState(false);
  const [activationStatus, setActivationStatus] = useState(null);
  const [simplePorts, setSimplePorts] = useState([]);
  const [showSimplePortSelector, setShowSimplePortSelector] = useState(false);

  // Load gateways on mount
  useEffect(() => {
    const loadGateways = async () => {
      try {
        const dbGateways = await fetchGatewayListFromDB();
        const normalized = (dbGateways || []).map((gw) => ({
          id: gw.id,
          name: gw.name,
        }));
        setGateways(normalized);
        // Pre-select first gateway if available and none is selected
        if (normalized.length > 0) {
          setFormData((prev) => {
            if (prev.gateway_id) return prev; // Keep existing selection
            return { ...prev, gateway_id: normalized[0].id };
          });
          setBatchFormData((prev) => {
            if (prev.gateway_id) return prev;
            return { ...prev, gateway_id: String(normalized[0].id) };
          });
          setSelectedGatewayIds((prev) => (prev.length > 0 ? prev : [normalized[0].id]));
        }
      } catch (err) {
        console.error('Failed to load gateway list:', err);
      }
    };
    loadGateways();
  }, []);

  // Load ports for the Simple Send gateway selector
  useEffect(() => {
    if (!formData.gateway_id) {
      setSimplePorts([]);
      return;
    }
    api.get(`/ports?gateway_id=${formData.gateway_id}`)
      .then((resp) =>
        setSimplePorts(
          (resp.data || []).filter((p) => !p.archived && !isServiceNumber(p))
        )
      )
      .catch(() => setSimplePorts([]));
  }, [formData.gateway_id]);

  useEffect(() => {
    if (!batchFormData.gateway_id) return;
    const primaryGatewayId = parseInt(batchFormData.gateway_id, 10);
    if (!Number.isFinite(primaryGatewayId)) return;
    setSelectedGatewayIds((prev) => (prev.length > 0 ? prev : [primaryGatewayId]));
  }, [batchFormData.gateway_id]);

  useEffect(() => {
    setSelectedSenderPorts([]);
    setSelectedReceiverPorts([]);
    setBatchFormData((prev) => ({ ...prev, sender_ports: '', receiver_ports: '' }));
  }, [selectedGatewayIds]);

  // --- helpers ---
  const digits = (s) => String(s || '').replace(/\D/g, '');

  // Normalize recipient to E.164-ish: allow leading +, otherwise digits only
  const normalizeRecipient = (raw) => {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('+')) return '+' + digits(trimmed);
    return digits(trimmed);
  };

  // Normalize and split ports - handles comma-separated values like "5,7" or "1,3,7"
  // Returns array of port specs, each can be a single number or range like "2-4"
  const normalizePorts = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return [];
    
    // Remove all whitespace
    const cleaned = s.replace(/\s+/g, '');
    if (!cleaned) return [];
    
    // Split by comma to handle multiple ports
    const parts = cleaned.split(',');
    const validPorts = [];
    
    for (const part of parts) {
      // Accept digits, letters, dots, hyphens — covers "64A", "1.01", "2-4"
      if (/^[0-9A-Za-z.\-]+$/.test(part) && part.length > 0) {
        validPorts.push(part);
      }
    }
    
    return validPorts;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });
    setDebug(null);
    setActivationStatus(null);

    try {
      const recipient = normalizeRecipient(formData.recipient);
      const ports = normalizePorts(formData.port);
      const msg = String(formData.message || '').trim();

      if (!recipient) throw new Error('Please enter a valid recipient number.');
      if (!msg) throw new Error('Message cannot be empty.');

      if (activateBeforeSend) {
        setActivationStatus('⏳ Activating port...');
      }

      const payload = {
        recipient,
        message: msg,
        ports: ports, // Array of port specs, e.g. ["5", "7"] or ["1", "2-4"]
        gateway_id: formData.gateway_id ? parseInt(formData.gateway_id, 10) : null,
        activate_first: activateBeforeSend,
      };

      // POST via axios client (auth added via interceptor)
      const resp = await api.post('/send_sms', payload);

      // success branch (HTTP 2xx)
      const data = resp?.data || {};
      if (data.activation_results && Array.isArray(data.activation_results)) {
        const summary = data.activation_results.map((r) =>
          `Port ${r.port}: ${r.status}${r.reason ? ` (${r.reason})` : ''}`
        ).join(', ');
        setActivationStatus(`✅ ${summary}`);
      } else if (activateBeforeSend) {
        setActivationStatus('✅ Sent.');
      }
      const gw = data.gateway_response || {};
      const parts = [];

      // Friendly message parts (all strings)
      if (typeof gw.code !== 'undefined') {
        parts.push(`Gateway code: ${gw.code}${gw.reason ? ` (${gw.reason})` : ''}`);
      }
      if (Array.isArray(gw.status) && gw.status.length) {
        parts.push(gw.status.map((s) => `tid ${s.tid}: ${s.status}`).join(' | '));
      }

      setStatus({
        type: 'success',
        message: data.message || (parts.length ? parts.join(' — ') : 'SMS queued to gateway.'),
      });
      setDebug({
        http_status: resp.status,
        request_payload: payload,
        response: data,
      });

      // reset form (keep gateway_id)
      setFormData((prev) => ({ recipient: '', message: '', port: '', gateway_id: prev.gateway_id }));
    } catch (err) {
      setActivationStatus(null);
      // axios error handling
      const httpStatus = err?.response?.status;
      const data = err?.response?.data;
      const backendDetail = (data && (data.detail || data.message)) || err.message || 'Failed to send SMS';

      // Try to extract gateway details (stringify safely)
      const gw = (data && data.gateway_response) || {};
      const pieces = [backendDetail];

      if (typeof data?.http_status !== 'undefined') {
        pieces.push(`HTTP ${data.http_status}`);
      } else if (typeof httpStatus !== 'undefined') {
        pieces.push(`HTTP ${httpStatus}`);
      }
      if (typeof gw.code !== 'undefined') {
        pieces.push(`Gateway code: ${gw.code}${gw.reason ? ` (${gw.reason})` : ''}`);
      }
      if (Array.isArray(gw.status) && gw.status.length) {
        pieces.push(gw.status.map((s) => `tid ${s.tid}: ${s.status}`).join(' | '));
      }

      setStatus({
        type: 'error',
        message: pieces.filter(Boolean).join(' — '),
      });

      setDebug({
        http_status: httpStatus,
        request_payload: {
          recipient: normalizeRecipient(formData.recipient),
          message: String(formData.message || '').trim(),
          ports: normalizePorts(formData.port),
        },
        response: data || null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getBatchGatewayIds = useCallback(() => {
    if (selectedGatewayIds.length > 0) return selectedGatewayIds;
    if (!batchFormData.gateway_id) return [];
    const parsed = parseInt(batchFormData.gateway_id, 10);
    return Number.isFinite(parsed) ? [parsed] : [];
  }, [selectedGatewayIds, batchFormData.gateway_id]);

  // Load ports for selected gateways
  useEffect(() => {
    const loadPorts = async () => {
      const gatewayIds = getBatchGatewayIds();
      if (gatewayIds.length === 0) {
        setPorts([]);
        return;
      }
      try {
        const responses = await Promise.all(
          gatewayIds.map((gatewayId) => api.get(`/ports?gateway_id=${gatewayId}`))
        );
        const merged = [];
        responses.forEach((resp) => {
          (resp.data || []).forEach((port) => merged.push(port));
        });
        const uniqueById = Array.from(new Map(merged.map((port) => [port.id, port])).values());
        const nonArchived = uniqueById.filter((p) => !p.archived && !isServiceNumber(p));
        console.log('Loaded ports:', nonArchived);
        setPorts(nonArchived);
      } catch (err) {
        console.error('Failed to load ports:', err);
        setPorts([]);
      }
    };
    loadPorts();
  }, [getBatchGatewayIds]);

  // Load receiver ports for receiver gateway
  useEffect(() => {
    const loadReceiverPorts = async () => {
      const receiverGatewayId = batchFormData.receiver_gateway_id || batchFormData.gateway_id;
      if (!receiverGatewayId) {
        setReceiverPorts([]);
        return;
      }
      try {
        const resp = await api.get(`/ports?gateway_id=${receiverGatewayId}`);
        const portsData = (resp.data || []).filter((p) => !p.archived);
        setReceiverPorts(portsData);
      } catch (err) {
        console.error('Failed to load receiver ports:', err);
        setReceiverPorts([]);
      }
    };
    loadReceiverPorts();
  }, [batchFormData.receiver_gateway_id, batchFormData.gateway_id]);

  // Load batch history
  const loadBatchHistory = async () => {
    setLoadingHistory(true);
    try {
      const resp = await api.get('/batch-sms/', { params: { limit: 20 } });
      setBatchHistory(resp.data || []);
    } catch (err) {
      console.error('Failed to load batch history:', err);
      console.error('Error details:', err.response?.data || err.message);
      setBatchHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load batch history on mount and when batch is created
  useEffect(() => {
    loadBatchHistory();
  }, []);

  useEffect(() => {
    if (batchId) {
      loadBatchHistory();
    }
  }, [batchId]);

  const filterPortsAcrossGateways = async (extraParams = {}) => {
    const gatewayIds = getBatchGatewayIds();
    if (!gatewayIds.length) {
      throw new Error('Please select at least one gateway first');
    }

    const responses = await Promise.all(
      gatewayIds.map((gatewayId) =>
        api.get('/ports/filter', {
          params: {
            gateway_id: gatewayId,
            exclude_archived: true,
            ...extraParams,
          },
        })
      )
    );

    const merged = responses.flatMap((resp) => resp.data || []);
    return Array.from(new Map(merged.map((port) => [port.id, port])).values());
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    setBatchLoading(true);
    setStatus({ type: '', message: '' });

    try {
      // Parse inputs - use selected ports if available, otherwise parse from text input
      const senderPorts = selectedSenderPorts.length > 0 
        ? selectedSenderPorts.map(p => p.id.toString())
        : batchFormData.sender_ports.split(',').map(s => s.trim()).filter(Boolean);
      const messagePool = batchFormData.message_pool.split('\n').map(s => s.trim()).filter(Boolean);

      if (!senderPorts.length) throw new Error('At least one sender port required');
      if (!messagePool.length) throw new Error('At least one message required in pool');

      const gatewayIds = getBatchGatewayIds();
      if (!gatewayIds.length) throw new Error('At least one gateway is required');

      const payload = {
        batch_name: batchFormData.batch_name || undefined,
        gateway_id: gatewayIds[0],
        gateway_ids: gatewayIds,
        mode: batchFormData.mode || 'standard',
        sender_ports: senderPorts,
        message_pool: messagePool,
        tracking_port_id: batchFormData.tracking_port_id ? parseInt(batchFormData.tracking_port_id, 10) : null,
        delay_between_sends_seconds: parseInt(batchFormData.delay_between_sends_seconds, 10) || 0,
        wait_for_activation_timeout_seconds: parseInt(batchFormData.wait_for_activation_timeout_seconds, 10) || 300,
      };

      // Add mode-specific fields
      if (batchFormData.mode === 'port_to_port') {
        const receiverPortsList = selectedReceiverPorts.length > 0
          ? selectedReceiverPorts.map(p => p.id.toString())
          : batchFormData.receiver_ports.split(',').map(s => s.trim()).filter(Boolean);
        if (!receiverPortsList.length) throw new Error('At least one receiver port required for port-to-port mode');
        payload.receiver_ports = receiverPortsList;
        // Add receiver_gateway_id if specified (optional - defaults to sender gateway)
        if (batchFormData.receiver_gateway_id) {
          payload.receiver_gateway_id = parseInt(batchFormData.receiver_gateway_id);
        }
      } else {
        const recipients = batchFormData.recipients.split('\n').map(s => s.trim()).filter(Boolean);
        if (!recipients.length) throw new Error('At least one recipient required');
        payload.recipients = recipients;
      }

      const resp = await api.post('/batch-sms/create', payload);
      const newBatchId = resp.data.batch_id;
      setBatchId(newBatchId);
      setStatus({
        type: 'success',
        message: `Batch created! ${resp.data.message} Batch ID: ${newBatchId}`,
      });
      
      // Immediately load batch status to show the table
      try {
        const statusResp = await api.get(`/batch-sms/${newBatchId}/status`);
        setBatchStatus(statusResp.data);
        setPreviousStatus(null); // Reset to track changes from start
        setEventLog([{
          time: new Date().toLocaleTimeString(),
          type: 'info',
          message: `🚀 Batch ${newBatchId} created with ${statusResp.data.total_sends} items`,
          itemId: null
        }]);
      } catch (err) {
        console.error('Failed to load initial batch status:', err);
      }
    } catch (err) {
      console.error('Batch SMS creation error:', err);
      console.error('Response:', err?.response);
      console.error('Status:', err?.response?.status);
      console.error('Data:', err?.response?.data);
      const errorMsg = err?.response?.data?.detail || err?.response?.data?.message || err.message || 'Failed to create batch';
      setStatus({ type: 'error', message: `Error: ${errorMsg} (Status: ${err?.response?.status || 'N/A'})` });
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchInputChange = (e) => {
    const { name, value } = e.target;
    setBatchFormData((prev) => ({ ...prev, [name]: value }));
  };

  const loadBatchStatus = async (id) => {
    if (!id) return;
    setLoadingStatus(true);
    try {
      const resp = await api.get(`/batch-sms/${id}/status`);
      setBatchStatus(resp.data);
    } catch (err) {
      console.error('Failed to load batch status:', err);
      setStatus({ type: 'error', message: `Failed to load batch status: ${err.message}` });
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    if (!batchId) return;
    
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    const loadBatchStatusWithEvents = async () => {
      setLoadingStatus(true);
      try {
        const response = await api.get(`/batch-sms/${batchId}/status`);
        const newStatus = response.data;
        
        // Check if all items have final delivery status (not pending)
        const allItemsHaveFinalStatus = newStatus.items && newStatus.items.every(item => {
          const deliveryStatus = item.delivery_status || 'pending';
          return deliveryStatus !== 'pending';
        });
        
        // Stop polling only if batch is completed AND all items have final delivery status
        if (newStatus.status === 'completed' && allItemsHaveFinalStatus) {
          setBatchStatus(newStatus);
          setPreviousStatus(newStatus);
          setLoadingStatus(false);
          // Clear the interval if it exists
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return; // Exit early, all done
        }
        
        // Track state changes and add to event log
        if (previousStatus && newStatus.items) {
          newStatus.items.forEach((item, idx) => {
            const prevItem = previousStatus.items?.[idx];
            if (!prevItem) return;
            
            const now = new Date().toLocaleTimeString();
            
            // Track sender port activation
            if (item.activation_status !== prevItem.activation_status) {
              const statusEmoji = item.activation_status === 'active' ? '✅' : 
                                 item.activation_status === 'activating' ? '🟡' : 
                                 item.activation_status === 'queued' ? '⏳' : '❌';
              setEventLog(prev => [...prev, {
                time: now,
                type: 'activation',
                message: `${statusEmoji} Sender port ${item.sender_port} activation: ${prevItem.activation_status || 'pending'} → ${item.activation_status}`,
                itemId: item.id
              }]);
            }
            
            // Track receiver port activation
            if (item.receiver_port && item.receiver_activation_status !== prevItem.receiver_activation_status) {
              const statusEmoji = item.receiver_activation_status === 'active' ? '✅' : 
                                 item.receiver_activation_status === 'activating' ? '🟡' : 
                                 item.receiver_activation_status === 'queued' ? '⏳' : '❌';
              setEventLog(prev => [...prev, {
                time: now,
                type: 'activation',
                message: `${statusEmoji} Receiver port ${item.receiver_port} activation: ${prevItem.receiver_activation_status || 'pending'} → ${item.receiver_activation_status}`,
                itemId: item.id
              }]);
            }
            
            // Track send status
            if (item.send_status !== prevItem.send_status) {
              const statusEmoji = item.send_status === 'sent' ? '📤' : 
                                 item.send_status === 'failed' ? '❌' : '⏳';
              setEventLog(prev => [...prev, {
                time: now,
                type: 'send',
                message: `${statusEmoji} Message sent: ${item.sender_port} → ${item.recipient} (Status: ${item.send_status})`,
                itemId: item.id
              }]);
            }
            
            // Track delivery status
            if (item.delivery_status !== prevItem.delivery_status) {
              const statusEmoji = item.delivery_status === 'delivered' ? '📥' : 
                                 item.delivery_status === 'timeout' ? '⏱️' : 
                                 item.delivery_status === 'failed' ? '❌' : '⏳';
              setEventLog(prev => [...prev, {
                time: now,
                type: 'delivery',
                message: `${statusEmoji} Delivery: ${item.recipient} (Status: ${item.delivery_status})`,
                itemId: item.id
              }]);
            }
          });
        } else if (newStatus.items && !previousStatus) {
          // Initial load - add batch created event
          const now = new Date().toLocaleTimeString();
          setEventLog([{
            time: now,
            type: 'info',
            message: `🚀 Batch ${batchId} created with ${newStatus.total_sends} items`,
            itemId: null
          }]);
        }
        
        setBatchStatus(newStatus);
        setPreviousStatus(newStatus);
      } catch (err) {
        console.error('Failed to load batch status:', err);
      } finally {
        setLoadingStatus(false);
      }
    };
    
    // Load status immediately
    loadBatchStatusWithEvents();
    
    // Poll more frequently when processing (every 2 seconds), less frequently when pending/completed (every 5 seconds)
    const pollInterval = batchStatus?.status === 'processing' ? 2000 : 5000;
    
    pollingIntervalRef.current = setInterval(() => {
      loadBatchStatusWithEvents();
    }, pollInterval);
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [batchId]);

  return (
    <div className="container-fluid px-3 py-2" style={{ fontSize: '13px' }}>
      <h4 className="mb-3" style={{ fontSize: '18px' }}>Send SMS</h4>

      {/* Mode selector */}
      <div className="mb-3">
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn btn-sm ${mode === 'simple' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('simple')}
          >
            Simple Send
          </button>
          <button
            type="button"
            className={`btn btn-sm ${mode === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('advanced')}
          >
            Advanced Batch (Activate & Track)
          </button>
        </div>
      </div>

      {status.message && (
        <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'} py-2 mb-3`}>
          {status.message}
        </div>
      )}

      {mode === 'simple' ? (
        <>
          {debug && (
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowDebug((v) => !v)}
              >
                {showDebug ? 'Hide details' : 'Show details'}
              </button>
              {showDebug && (
                <pre className="mt-2 p-2 bg-light border rounded" style={{ fontSize: 12, maxHeight: 260, overflow: 'auto' }}>
{JSON.stringify(debug, null, 2)}
                </pre>
              )}
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="recipient" className="form-label">Recipient Number</label>
              <input
                type="text"
                id="recipient"
                name="recipient"
                value={formData.recipient}
                onChange={handleInputChange}
                placeholder="e.g., +447123456789 or 447123456789"
                required
                className="form-control form-control-sm"
                style={{ maxWidth: '320px' }}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="gateway_id" className="form-label">Gateway</label>
              <select
                id="gateway_id"
                name="gateway_id"
                value={formData.gateway_id}
                onChange={handleInputChange}
                className="form-select form-select-sm"
                style={{ maxWidth: '320px' }}
                required
              >
                {gateways.length === 0 ? (
                  <option value="">Loading gateways...</option>
                ) : (
                  gateways.map((gw) => (
                    <option key={gw.id} value={gw.id}>
                      {gw.name} (ID: {gw.id})
                    </option>
                  ))
                )}
              </select>
              <div className="form-text">
                Select the gateway to send the message from.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">From Port (Channel)</label>
              {simplePorts.length > 0 && (
                <div className="mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowSimplePortSelector((v) => !v)}
                  >
                    {showSimplePortSelector ? 'Hide' : 'Pick'} Port
                    {formData.port ? ` — selected: ${formData.port}` : ' (optional)'}
                  </button>
                  {formData.port && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary ms-2"
                      onClick={() => setFormData((prev) => ({ ...prev, port: '' }))}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
              {showSimplePortSelector && simplePorts.length > 0 && (
                <div className="border rounded p-2 mb-2" style={{ maxHeight: '220px', overflowY: 'auto', maxWidth: '400px', backgroundColor: '#f8f9fa' }}>
                  <div className="row g-1">
                    {simplePorts.map((port) => {
                      const selected = formData.port === port.port_number;
                      return (
                        <div key={port.id} className="col-6">
                          <button
                            type="button"
                            className={`btn btn-sm w-100 text-start ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: '12px' }}
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, port: selected ? '' : port.port_number }));
                              setShowSimplePortSelector(false);
                            }}
                          >
                            {port.port_number}
                            {port.assigned_number ? ` (${port.assigned_number})` : ''}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <input
                type="text"
                id="port"
                name="port"
                value={formData.port}
                onChange={handleInputChange}
                placeholder="e.g., 1 or 2-4 or 1,3,7 (leave empty for auto)"
                className="form-control form-control-sm"
                style={{ maxWidth: '320px' }}
              />
              <div className="form-text">
                Optional. Pick a port above or type a channel number. Leave empty to let the gateway choose.
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="activateBeforeSend"
                  checked={activateBeforeSend}
                  onChange={(e) => setActivateBeforeSend(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="activateBeforeSend">
                  Activate port before sending
                </label>
              </div>
              <div className="form-text">
                If checked, the selected port will be activated before sending the message.
              </div>
              {activationStatus && (
                <div className={`alert ${activationStatus.includes('✅') ? 'alert-success' : activationStatus.includes('⏳') ? 'alert-info' : 'alert-warning'} py-2 mt-2`}>
                  {activationStatus}
                </div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="message" className="form-label">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="4"
                required
                placeholder="Enter your message here"
                className="form-control form-control-sm"
                style={{ maxWidth: '600px', resize: 'none' }}
              />
              <div className="form-text">{formData.message.length} characters</div>
            </div>

                <button type="submit" disabled={loading} className="btn btn-primary btn-sm">
                  {loading ? 'Sending...' : 'Send SMS'}
                </button>
              </form>
            </div>
          </div>
        </>
      ) : (
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleBatchSubmit}>
              <div className="mb-3">
                <label htmlFor="batch_name" className="form-label">Batch Name (Optional)</label>
                <input
                  type="text"
                  id="batch_name"
                  name="batch_name"
                  value={batchFormData.batch_name}
                  onChange={handleBatchInputChange}
                  className="form-control form-control-sm"
                  style={{ maxWidth: '400px' }}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="batch_mode" className="form-label">Mode *</label>
                <select
                  id="batch_mode"
                  name="mode"
                  value={batchFormData.mode}
                  onChange={handleBatchInputChange}
                  required
                  className="form-select form-select-sm"
                  style={{ maxWidth: '400px' }}
                >
                  <option value="standard">Standard: Sender Ports × Recipients</option>
                  <option value="port_to_port">Port-to-Port: Sender Ports → Random Receiver Ports</option>
                </select>
                <div className="form-text">
                  {batchFormData.mode === 'standard' && 'Each recipient gets a message from each sender port. Sender ports are activated before sending.'}
                  {batchFormData.mode === 'port_to_port' && 'Each sender port sends to a randomly assigned receiver port. Both sender and receiver ports are activated and confirmed active before sending.'}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Gateway(s) * <span className="text-muted small">- select one or more</span>
                </label>
                <div className="d-flex gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowGatewaySelector((v) => !v)}
                  >
                    {showGatewaySelector ? 'Hide' : 'Show'} Gateway Selector ({selectedGatewayIds.length || 'none'} selected)
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      const allIds = gateways.map((gw) => gw.id);
                      setSelectedGatewayIds(allIds);
                      setBatchFormData((prev) => ({ ...prev, gateway_id: allIds.length > 0 ? String(allIds[0]) : '' }));
                    }}
                  >
                    Select All Gateways
                  </button>
                  {selectedGatewayIds.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => {
                        setSelectedGatewayIds([]);
                        setBatchFormData((prev) => ({ ...prev, gateway_id: '' }));
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>

                {showGatewaySelector && (
                  <div className="border rounded p-3 mb-2" style={{ maxWidth: '500px', backgroundColor: '#f8f9fa' }}>
                    <div className="row g-2">
                      {gateways.map((gw) => {
                        const isSelected = selectedGatewayIds.includes(gw.id);
                        return (
                          <div key={gw.id} className="col-12">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`gw-select-${gw.id}`}
                                checked={isSelected}
                                onChange={(e) => {
                                  let nextIds;
                                  if (e.target.checked) {
                                    nextIds = [...selectedGatewayIds, gw.id];
                                  } else {
                                    nextIds = selectedGatewayIds.filter((id) => id !== gw.id);
                                  }
                                  setSelectedGatewayIds(nextIds);
                                  setBatchFormData((prev) => ({
                                    ...prev,
                                    gateway_id: nextIds.length > 0 ? String(nextIds[0]) : '',
                                  }));
                                }}
                              />
                              <label className="form-check-label" htmlFor={`gw-select-${gw.id}`}>
                                <strong>{gw.name}</strong> <span className="text-muted">(ID: {gw.id})</span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedGatewayIds.length > 0 ? (
                  <div className="form-text">
                    Selected: {gateways.filter((gw) => selectedGatewayIds.includes(gw.id)).map((gw) => gw.name).join(', ')}
                  </div>
                ) : (
                  <div className="alert alert-warning py-2" style={{ maxWidth: '400px' }}>
                    Please select at least one gateway above.
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Sender Ports *</label>
                <div className="d-flex gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (getBatchGatewayIds().length === 0) {
                        alert('Please select at least one gateway first');
                        return;
                      }
                      setShowPortSelector(!showPortSelector);
                    }}
                  >
                    {showPortSelector ? 'Hide' : 'Show'} Port Selector ({selectedSenderPorts.length} selected)
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedSenderPorts([]);
                      setBatchFormData(prev => ({ ...prev, sender_ports: '' }));
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setFilteringPorts(true);
                      try {
                        const matchingPorts = await filterPortsAcrossGateways({
                          min_days: filterDaysMin,
                          max_days: filterDaysMax,
                          has_assigned_number: true,
                          exclude_dead: true,
                        });
                        if (matchingPorts.length === 0) {
                          alert(`No ports found matching criteria:\n- Gateways: ${getBatchGatewayIds().join(', ')}\n- Days since: ${filterDaysMin}-${filterDaysMax}\n- Has assigned number: Yes`);
                          return;
                        }
                        // Auto-select these ports
                        setSelectedSenderPorts(matchingPorts);
                        setBatchFormData(prev => ({
                          ...prev,
                          sender_ports: matchingPorts.map(p => p.port_number).join(',')
                        }));
                        alert(`✅ Auto-selected ${matchingPorts.length} port(s) matching your criteria!`);
                      } catch (err) {
                        console.error('Failed to filter ports:', err);
                        alert(`Error filtering ports: ${err?.response?.data?.detail || err.message}`);
                      } finally {
                        setFilteringPorts(false);
                      }
                    }}
                    disabled={filteringPorts || getBatchGatewayIds().length === 0}
                  >
                    {filteringPorts ? 'Filtering...' : `Auto-Select (${filterDaysMin}-${filterDaysMax} days)`}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-info"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLoadingSenderAssignedPorts(true);
                      try {
                        const assignedPorts = await filterPortsAcrossGateways({
                          has_assigned_number: true,
                          exclude_dead: true,
                        });
                        if (assignedPorts.length === 0) {
                          alert(`No assigned ports found in gateways ${getBatchGatewayIds().join(', ')}`);
                          return;
                        }
                        // Auto-select these ports as sender ports
                        setSelectedSenderPorts(assignedPorts);
                        setBatchFormData(prev => ({
                          ...prev,
                          sender_ports: assignedPorts.map(p => p.port_number).join(',')
                        }));
                        alert(`✅ Auto-selected ${assignedPorts.length} sender port(s) from selected gateway(s)!`);
                      } catch (err) {
                        console.error('Failed to filter sender assigned ports:', err);
                        alert(`Error filtering ports: ${err?.response?.data?.detail || err.message}`);
                      } finally {
                        setLoadingSenderAssignedPorts(false);
                      }
                    }}
                    disabled={loadingSenderAssignedPorts || getBatchGatewayIds().length === 0}
                  >
                    {loadingSenderAssignedPorts ? 'Loading...' : 'Select All Assigned Ports'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLoadingSenderAssignedPorts(true);
                      try {
                        const unusedPorts = await filterPortsAcrossGateways({
                          has_assigned_number: true,
                          only_unused: true,
                          exclude_dead: false,
                        });
                        if (unusedPorts.length === 0) {
                          alert(`No unused ports found in gateways ${getBatchGatewayIds().join(', ')}`);
                          return;
                        }
                        setSelectedSenderPorts(unusedPorts);
                        setBatchFormData(prev => ({
                          ...prev,
                          sender_ports: unusedPorts.map(p => p.port_number).join(',')
                        }));
                        alert(`✅ Auto-selected ${unusedPorts.length} unused sender port(s) (never sent before)!`);
                      } catch (err) {
                        console.error('Failed to filter unused ports:', err);
                        alert(`Error: ${err?.response?.data?.detail || err.message}`);
                      } finally {
                        setLoadingSenderAssignedPorts(false);
                      }
                    }}
                    disabled={loadingSenderAssignedPorts || getBatchGatewayIds().length === 0}
                  >
                    {loadingSenderAssignedPorts ? 'Loading...' : '🆕 Select Unused Ports'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLoadingSenderAssignedPorts(true);
                      try {
                        const healthyPorts = await filterPortsAcrossGateways({
                          has_assigned_number: true,
                          exclude_dead: true,
                          only_healthy: true,
                        });
                        if (healthyPorts.length === 0) {
                          alert(`No healthy ports found in gateways ${getBatchGatewayIds().join(', ')}`);
                          return;
                        }
                        setSelectedSenderPorts(healthyPorts);
                        setBatchFormData(prev => ({
                          ...prev,
                          sender_ports: healthyPorts.map(p => p.port_number).join(',')
                        }));
                        alert(`✅ Auto-selected ${healthyPorts.length} healthy port(s) (no dead/failed ports)!`);
                      } catch (err) {
                        console.error('Failed to filter healthy ports:', err);
                        alert(`Error: ${err?.response?.data?.detail || err.message}`);
                      } finally {
                        setLoadingSenderAssignedPorts(false);
                      }
                    }}
                    disabled={loadingSenderAssignedPorts || getBatchGatewayIds().length === 0}
                  >
                    {loadingSenderAssignedPorts ? 'Loading...' : '💚 Select All Healthy Ports'}
                  </button>
                </div>
                <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                  <label className="form-label mb-0" style={{ fontSize: '12px' }}>
                    Auto-select filter:
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: '80px' }}
                    value={filterDaysMin}
                    onChange={(e) => setFilterDaysMin(parseInt(e.target.value) || 0)}
                    min="0"
                    placeholder="Min"
                  />
                  <span style={{ fontSize: '12px' }}>to</span>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: '80px' }}
                    value={filterDaysMax}
                    onChange={(e) => setFilterDaysMax(parseInt(e.target.value) || 0)}
                    min="0"
                    placeholder="Max"
                  />
                  <span style={{ fontSize: '12px' }}>days since last message</span>
                </div>
                {getBatchGatewayIds().length === 0 && (
                  <div className="alert alert-warning py-2 mb-2">
                    Please select at least one gateway first to use the port selector
                  </div>
                )}
                {showPortSelector && getBatchGatewayIds().length > 0 && ports.length === 0 && (
                  <div className="alert alert-info py-2 mb-2">
                    Loading ports...
                  </div>
                )}
                {showPortSelector && getBatchGatewayIds().length > 0 && ports.length > 0 && (
                  <div className="border rounded p-3 mb-2" style={{ maxHeight: '300px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Select Sender Ports:</strong>
                      <div className="d-flex gap-2 align-items-center">
                        <span className="text-muted small">{selectedSenderPorts.length} selected</span>
                        <div className="form-check form-check-inline mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="showDeadPorts"
                            checked={showDeadPorts}
                            onChange={(e) => setShowDeadPorts(e.target.checked)}
                          />
                          <label className="form-check-label small" htmlFor="showDeadPorts">
                            Show dead ports
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="row g-2">
                      {ports
                        .filter((port) => showDeadPorts || !port.is_dead)
                        .map((port) => {
                          const isSelected = selectedSenderPorts.some(p => p.id === port.id);
                          const isDead = port.is_dead || false;
                          return (
                            <div key={port.id} className="col-md-3 col-sm-4 col-6">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`sender-port-${port.id}`}
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedSenderPorts([...selectedSenderPorts, port]);
                                    } else {
                                      setSelectedSenderPorts(selectedSenderPorts.filter(p => p.id !== port.id));
                                    }
                                  }}
                                  disabled={isDead}
                                />
                                <label
                                  className={`form-check-label ${isDead ? 'text-muted text-decoration-line-through' : ''}`}
                                  htmlFor={`sender-port-${port.id}`}
                                >
                                  {port.port_number} {port.assigned_number ? `(${port.assigned_number})` : ''}
                                  {isDead && <span className="badge bg-danger ms-1" style={{ fontSize: '9px' }}>DEAD</span>}
                                </label>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
                <input
                  type="text"
                  id="sender_ports"
                  name="sender_ports"
                  value={selectedSenderPorts.length > 0 
                    ? selectedSenderPorts.map(p => p.port_number).join(',')
                    : batchFormData.sender_ports}
                  onChange={(e) => {
                    handleBatchInputChange(e);
                    // Clear selected ports if user manually edits
                    if (selectedSenderPorts.length > 0) {
                      setSelectedSenderPorts([]);
                    }
                  }}
                  placeholder="e.g., 4A,6C,5C or use selector above"
                  required
                  className="form-control form-control-sm"
                  style={{ maxWidth: '400px' }}
                />
                <div className="form-text">
                  {selectedSenderPorts.length > 0 
                    ? `${selectedSenderPorts.length} port(s) selected. You can also type port numbers manually.`
                    : 'Use the port selector, auto-select button, or enter port numbers (like 4A, 6C, 5C) manually. Ports will be activated and confirmed active before sending.'}
                  {batchFormData.mode === 'standard' && ' Each recipient gets a message from each port.'}
                  {batchFormData.mode === 'port_to_port' && ' Each port will send to a randomly assigned receiver port.'}
                </div>
                <div className="mt-2 d-flex align-items-center gap-2">
                  <label className="form-label mb-0" style={{ fontSize: '12px' }}>
                    Auto-select filter:
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: '80px' }}
                    value={filterDaysMin}
                    onChange={(e) => setFilterDaysMin(parseInt(e.target.value) || 0)}
                    min="0"
                    placeholder="Min"
                  />
                  <span style={{ fontSize: '12px' }}>to</span>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: '80px' }}
                    value={filterDaysMax}
                    onChange={(e) => setFilterDaysMax(parseInt(e.target.value) || 0)}
                    min="0"
                    placeholder="Max"
                  />
                  <span style={{ fontSize: '12px' }}>days since last message</span>
                </div>
              </div>

              {batchFormData.mode === 'port_to_port' ? (
                <>
                  <div className="mb-3">
                    <label htmlFor="receiver_gateway_id" className="form-label">Receiver Gateway (Optional)</label>
                    <select
                      className="form-control form-control-sm"
                      id="receiver_gateway_id"
                      name="receiver_gateway_id"
                      value={batchFormData.receiver_gateway_id}
                      onChange={handleBatchInputChange}
                      style={{ maxWidth: '400px' }}
                    >
                      <option value="">Same as Sender Gateway</option>
                      {gateways.map((gw) => (
                        <option key={gw.id} value={gw.id}>
                          {gw.name} (ID: {gw.id})
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Select the gateway for receiver ports. If not specified, uses the same gateway as sender ports.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Receiver Ports *</label>
                    <div className="d-flex gap-2 mb-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!batchFormData.receiver_gateway_id && !batchFormData.gateway_id) {
                            alert('Please select a gateway first');
                            return;
                          }
                          setShowReceiverPortSelector(!showReceiverPortSelector);
                        }}
                      >
                        {showReceiverPortSelector ? 'Hide' : 'Show'} Port Selector ({selectedReceiverPorts.length} selected)
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedReceiverPorts([]);
                          setBatchFormData(prev => ({ ...prev, receiver_ports: '' }));
                        }}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-info"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const receiverGatewayId = batchFormData.receiver_gateway_id || batchFormData.gateway_id;
                          if (!receiverGatewayId) {
                            alert('Please select a gateway first (sender or receiver gateway)');
                            return;
                          }
                          setLoadingRecipientPorts(true);
                          try {
                            const resp = await api.get('/ports/filter', {
                              params: {
                                gateway_id: receiverGatewayId,
                                has_assigned_number: true,
                                exclude_dead: true,
                                exclude_archived: true
                              }
                            });
                            const assignedPorts = resp.data || [];
                            if (assignedPorts.length === 0) {
                              alert(`No assigned ports found in gateway ${receiverGatewayId}`);
                              return;
                            }
                            // Auto-select these ports as receiver ports
                            setSelectedReceiverPorts(assignedPorts);
                            setBatchFormData(prev => ({
                              ...prev,
                              receiver_ports: assignedPorts.map(p => p.port_number).join(',')
                            }));
                            alert(`✅ Auto-selected ${assignedPorts.length} receiver port(s) from assigned ports in gateway ${receiverGatewayId}!`);
                          } catch (err) {
                            console.error('Failed to filter receiver ports:', err);
                            alert(`Error filtering ports: ${err.response?.data?.detail || err.message}`);
                          } finally {
                            setLoadingRecipientPorts(false);
                          }
                        }}
                        disabled={loadingRecipientPorts || (!batchFormData.receiver_gateway_id && !batchFormData.gateway_id)}
                      >
                        {loadingRecipientPorts ? 'Loading...' : 'Select All Assigned Ports'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const receiverGatewayId = batchFormData.receiver_gateway_id || batchFormData.gateway_id;
                          if (!receiverGatewayId) {
                            alert('Please select a gateway first (sender or receiver gateway)');
                            return;
                          }
                          setLoadingRecipientPorts(true);
                          try {
                            const resp = await api.get('/ports/filter', {
                              params: {
                                gateway_id: receiverGatewayId,
                                has_assigned_number: true,
                                exclude_dead: true,
                                exclude_archived: true,
                                only_healthy: true
                              }
                            });
                            const healthyPorts = resp.data || [];
                            if (healthyPorts.length === 0) {
                              alert(`No healthy receiver ports found in gateway ${receiverGatewayId}`);
                              return;
                            }
                            setSelectedReceiverPorts(healthyPorts);
                            setBatchFormData(prev => ({
                              ...prev,
                              receiver_ports: healthyPorts.map(p => p.port_number).join(',')
                            }));
                            alert(`✅ Auto-selected ${healthyPorts.length} healthy receiver port(s) (no dead/failed ports)!`);
                          } catch (err) {
                            console.error('Failed to filter healthy receiver ports:', err);
                            alert(`Error: ${err.response?.data?.detail || err.message}`);
                          } finally {
                            setLoadingRecipientPorts(false);
                          }
                        }}
                        disabled={loadingRecipientPorts || (!batchFormData.receiver_gateway_id && !batchFormData.gateway_id)}
                      >
                        {loadingRecipientPorts ? 'Loading...' : '💚 Select All Healthy Ports'}
                      </button>
                    </div>
                    {!batchFormData.receiver_gateway_id && !batchFormData.gateway_id && (
                      <div className="alert alert-warning py-2 mb-2">
                        Please select a gateway first to use the port selector
                      </div>
                    )}
                    {showReceiverPortSelector && (batchFormData.receiver_gateway_id || batchFormData.gateway_id) && receiverPorts.length === 0 && (
                      <div className="alert alert-info py-2 mb-2">
                        Loading ports...
                      </div>
                    )}
                    {showReceiverPortSelector && (batchFormData.receiver_gateway_id || batchFormData.gateway_id) && receiverPorts.length > 0 && (
                      <div className="border rounded p-3 mb-2" style={{ maxHeight: '300px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>Select Receiver Ports:</strong>
                          <span className="text-muted small">{selectedReceiverPorts.length} selected</span>
                        </div>
                        <div className="row g-2">
                          {receiverPorts.map((port) => {
                            const isSelected = selectedReceiverPorts.some(p => p.id === port.id);
                            return (
                              <div key={port.id} className="col-md-3 col-sm-4 col-6">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`receiver-port-${port.id}`}
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedReceiverPorts([...selectedReceiverPorts, port]);
                                      } else {
                                        setSelectedReceiverPorts(selectedReceiverPorts.filter(p => p.id !== port.id));
                                      }
                                    }}
                                  />
                                  <label className="form-check-label" htmlFor={`receiver-port-${port.id}`}>
                                    {port.port_number} {port.assigned_number ? `(${port.assigned_number})` : ''}
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <input
                      type="text"
                      id="receiver_ports"
                      name="receiver_ports"
                      value={selectedReceiverPorts.length > 0
                        ? selectedReceiverPorts.map(p => p.port_number).join(',')
                        : batchFormData.receiver_ports}
                      onChange={(e) => {
                        handleBatchInputChange(e);
                        // Clear selected ports if user manually edits
                        if (selectedReceiverPorts.length > 0) {
                          setSelectedReceiverPorts([]);
                        }
                      }}
                      placeholder="e.g., 64A or use selector above"
                      required={batchFormData.mode === 'port_to_port'}
                      className="form-control form-control-sm"
                      style={{ maxWidth: '400px' }}
                    />
                    <div className="form-text">
                      {selectedReceiverPorts.length > 0
                        ? `${selectedReceiverPorts.length} port(s) selected via selector. You can also type port numbers manually.`
                        : 'Use the port selector above or enter receiver port numbers manually. Each sender port will be randomly assigned to one receiver port. Both ports will be activated before sending.'}
                    </div>
                  </div>
                </>
              ) : (
                <div className="mb-3">
                  <label htmlFor="recipients" className="form-label">Recipients * (one per line)</label>
                  <div className="d-flex gap-2 mb-2">
                    <select
                      className="form-control form-control-sm"
                      style={{ width: '200px' }}
                      value={recipientGatewayId}
                      onChange={(e) => setRecipientGatewayId(e.target.value)}
                    >
                      <option value="">Select Gateway for Recipients</option>
                      {gateways.map((gw) => (
                        <option key={gw.id} value={gw.id}>
                          {gw.name} (ID: {gw.id})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-info"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!recipientGatewayId) {
                          alert('Please select a gateway first');
                          return;
                        }
                        setLoadingRecipientPorts(true);
                        try {
                          const resp = await api.get('/ports/filter', {
                            params: {
                              gateway_id: recipientGatewayId,
                              has_assigned_number: true,
                              exclude_dead: true
                            }
                          });
                          const assignedPorts = resp.data || [];
                          if (assignedPorts.length === 0) {
                            alert(`No assigned ports found in gateway ${recipientGatewayId}`);
                            return;
                          }
                          // Get assigned numbers (recipients) from the ports
                          const recipientNumbers = assignedPorts
                            .map(p => p.assigned_number)
                            .filter(num => num && num.trim() !== '');
                          if (recipientNumbers.length === 0) {
                            alert('No assigned numbers found in selected ports');
                            return;
                          }
                          // Set recipients field with one number per line
                          setBatchFormData(prev => ({
                            ...prev,
                            recipients: recipientNumbers.join('\n')
                          }));
                          alert(`✅ Auto-selected ${recipientNumbers.length} recipient(s) from assigned ports in gateway ${recipientGatewayId}!`);
                        } catch (err) {
                          console.error('Failed to load recipient ports:', err);
                          alert(`Error loading ports: ${err.response?.data?.detail || err.message}`);
                        } finally {
                          setLoadingRecipientPorts(false);
                        }
                      }}
                      disabled={loadingRecipientPorts || !recipientGatewayId}
                    >
                      {loadingRecipientPorts ? 'Loading...' : 'Select All Assigned Ports'}
                    </button>
                  </div>
                  <textarea
                    id="recipients"
                    name="recipients"
                    value={batchFormData.recipients}
                    onChange={handleBatchInputChange}
                    rows="5"
                    required={batchFormData.mode !== 'port_to_port'}
                    placeholder="07931920749&#10;+447912345678&#10;447123456789&#10;&#10;Or use 'Select All Assigned Ports' button above"
                    className="form-control form-control-sm"
                    style={{ maxWidth: '400px' }}
                  />
                  <div className="form-text">
                    One phone number per line. Each will receive messages from all sender ports. Use the button above to auto-select all assigned ports from a gateway.
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label htmlFor="message_pool" className="form-label">Message Pool * (one per line, randomly selected)</label>
                <textarea
                  id="message_pool"
                  name="message_pool"
                  value={batchFormData.message_pool}
                  onChange={handleBatchInputChange}
                  rows="5"
                  required
                  placeholder="Hello, how are you?&#10;Hi there!&#10;Just checking in."
                  className="form-control form-control-sm"
                  style={{ maxWidth: '600px' }}
                />
                <div className="form-text">One message per line. A random message will be selected for each send.</div>
              </div>

              <div className="mb-3">
                <label htmlFor="tracking_port_id" className="form-label">Tracking Port (Optional - for delivery reports)</label>
                <select
                  id="tracking_port_id"
                  name="tracking_port_id"
                  value={batchFormData.tracking_port_id}
                  onChange={handleBatchInputChange}
                  className="form-select form-select-sm"
                  style={{ maxWidth: '400px' }}
                  disabled={getBatchGatewayIds().length === 0}
                >
                  <option value="">
                    {getBatchGatewayIds().length === 0
                      ? 'Select gateway(s) first'
                      : ports.length === 0 
                        ? 'No ports available' 
                        : 'None'}
                  </option>
                  {ports.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.port_number} - {p.assigned_number || 'No number'}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  {getBatchGatewayIds().length === 0
                    ? 'Select one or more gateways first to see available ports.'
                    : `Port to monitor for delivery reports. ${ports.length} port(s) available. Leave empty if not tracking delivery.`}
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="delay_between_sends_seconds" className="form-label">Delay Between Sends (seconds)</label>
                <input
                  type="number"
                  id="delay_between_sends_seconds"
                  name="delay_between_sends_seconds"
                  value={batchFormData.delay_between_sends_seconds}
                  onChange={handleBatchInputChange}
                  min="0"
                  className="form-control form-control-sm"
                  style={{ maxWidth: '200px' }}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="wait_for_activation_timeout_seconds" className="form-label">Activation Timeout (seconds)</label>
                <input
                  type="number"
                  id="wait_for_activation_timeout_seconds"
                  name="wait_for_activation_timeout_seconds"
                  value={batchFormData.wait_for_activation_timeout_seconds}
                  onChange={handleBatchInputChange}
                  min="60"
                  className="form-control form-control-sm"
                  style={{ maxWidth: '200px' }}
                />
                <div className="form-text">Maximum time to wait for port activation before giving up.</div>
              </div>

              <button type="submit" disabled={batchLoading} className="btn btn-primary btn-sm">
                {batchLoading ? 'Creating Batch...' : 'Create Batch SMS Job'}
              </button>

              {batchId && (
                <div className="mt-3">
                  <div className="alert alert-info mb-0">
                    <strong>Batch ID: {batchId}</strong> - Status updates automatically every 2-5 seconds
                  </div>
                </div>
              )}

              {(batchId || batchStatus) && (
                <div className="mt-4">
                  {/* Event Log */}
                  <div className="card mb-3">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Real-Time Event Log</h5>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setEventLog([])}
                      >
                        Clear Log
                      </button>
                    </div>
                    <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto', backgroundColor: '#f8f9fa', fontFamily: 'monospace', fontSize: '12px' }}>
                      {eventLog.length === 0 ? (
                        <div className="text-muted">No events yet. Events will appear here as the batch processes...</div>
                      ) : (
                        eventLog.map((event, idx) => (
                          <div key={idx} className="mb-1">
                            <span className="text-muted">[{event.time}]</span>{' '}
                            <span className={event.type === 'activation' ? 'text-primary' : 
                                            event.type === 'send' ? 'text-success' : 
                                            event.type === 'delivery' ? 'text-info' : 'text-secondary'}>
                              {event.message}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Batch Status Table */}
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        Batch Status {batchStatus ? `(ID: ${batchStatus.batch_id})` : `(ID: ${batchId} - Loading...)`}
                      </h5>
                      {loadingStatus && <span className="badge bg-info">Refreshing...</span>}
                    </div>
                    <div className="card-body">
                      {!batchStatus ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 text-muted">Loading batch status...</p>
                        </div>
                      ) : (
                        <div>
                      <div className="row mb-3">
                      <div className="col-md-3">
                        <strong>Status:</strong> 
                        <span className={`badge ms-2 ${
                          batchStatus.status === 'completed' ? 'bg-success' :
                          batchStatus.status === 'processing' ? 'bg-warning' :
                          batchStatus.status === 'paused' ? 'bg-info' :
                          batchStatus.status === 'failed' ? 'bg-danger' : 'bg-secondary'
                        }`}>
                          {batchStatus.status}
                        </span>
                      </div>
                      <div className="col-md-3">
                        <strong>Total:</strong> {batchStatus.total_sends}
                      </div>
                      <div className="col-md-3">
                        <strong>Completed:</strong> <span className="text-success">{batchStatus.completed_sends}</span>
                      </div>
                      <div className="col-md-3">
                        <strong>Failed:</strong> <span className="text-danger">{batchStatus.failed_sends}</span>
                      </div>
                    </div>
                    <div className="mb-3 d-flex gap-2">
                      {batchStatus.status === 'processing' && (
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          onClick={async () => {
                            try {
                              await api.post(`/batch-sms/${batchStatus.batch_id}/pause`);
                              setStatus({ type: 'success', message: 'Batch paused successfully' });
                              // Reload status
                              await loadBatchStatus(batchStatus.batch_id);
                            } catch (err) {
                              setStatus({ type: 'error', message: `Failed to pause batch: ${err.response?.data?.detail || err.message}` });
                            }
                          }}
                        >
                          ⏸️ Pause Batch
                        </button>
                      )}
                      {batchStatus.status === 'paused' && (
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={async () => {
                            try {
                              const resp = await api.post(`/batch-sms/${batchStatus.batch_id}/resume`);
                              setStatus({ type: 'success', message: resp.data.message || 'Batch resumed successfully' });
                              // Reload status
                              await loadBatchStatus(batchStatus.batch_id);
                            } catch (err) {
                              setStatus({ type: 'error', message: `Failed to resume batch: ${err.response?.data?.detail || err.message}` });
                            }
                          }}
                        >
                          ▶️ Resume Batch
                        </button>
                      )}
                    </div>

                    {batchStatus.items && batchStatus.items.length > 0 && (
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead>
                            <tr>
                              <th>Recipient</th>
                              <th>Sender Port</th>
                              <th>Gateway</th>
                              <th>Receiver Port</th>
                              <th>Message</th>
                              <th>Sender Activation</th>
                              <th>Receiver Activation</th>
                              <th>Send Status</th>
                              <th>Delivery Status</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batchStatus.items.map((item) => (
                              <tr key={item.id}>
                                <td>{item.recipient}</td>
                                <td><strong>{item.sender_port || '-'}</strong></td>
                                <td>
                                  <span className="badge bg-secondary" style={{ fontSize: '10px' }}>
                                    {item.sender_gateway_id
                                      ? (gateways.find((gw) => gw.id === item.sender_gateway_id)?.name || `GW ${item.sender_gateway_id}`)
                                      : '-'}
                                  </span>
                                </td>
                                <td><strong>{item.receiver_port || '-'}</strong></td>
                                <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {item.message}
                                </td>
                                <td>
                                  <BatchActivationBadge status={item.activation_status} />
                                </td>
                                <td>
                                  {item.receiver_port ? (
                                    <BatchActivationBadge status={item.receiver_activation_status} />
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <span className={`badge ${
                                    item.send_status === 'sent' ? 'bg-success' :
                                    item.send_status === 'failed' ? 'bg-danger' : 'bg-secondary'
                                  }`}>
                                    {item.send_status || 'pending'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${
                                    item.delivery_status === 'delivered' ? 'bg-success' :
                                    item.delivery_status === 'timeout' ? 'bg-warning' :
                                    item.delivery_status === 'failed' ? 'bg-danger' : 'bg-secondary'
                                  }`}>
                                    {item.delivery_status || 'pending'}
                                  </span>
                                </td>
                                <td style={{ maxWidth: '200px', fontSize: '11px' }}>
                                  {item.error_message ? (
                                    <span className="text-danger" title={item.error_message}>
                                      {item.error_message.substring(0, 50)}...
                                    </span>
                                  ) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Batch History Panel */}
      <div className="mt-4">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Batch History</h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={loadBatchHistory}
              disabled={loadingHistory}
            >
              {loadingHistory ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          <div className="card-body">
            {loadingHistory ? (
              <div className="text-center py-3">Loading batch history...</div>
            ) : batchHistory.length === 0 ? (
              <div className="text-center text-muted py-3">No batches yet</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Mode</th>
                      <th>Gateway</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchHistory.map((batch) => (
                      <tr 
                        key={batch.id}
                        className={batch.id === batchId ? 'table-info' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setBatchId(batch.id);
                          setBatchStatus(null);
                          setEventLog([]);
                          setPreviousStatus(null);
                        }}
                      >
                        <td><strong>{batch.id}</strong></td>
                        <td>{batch.batch_name || '-'}</td>
                        <td>
                          <span className="badge bg-secondary">
                            {batch.mode === 'port_to_port' ? 'Port-to-Port' : 'Standard'}
                          </span>
                        </td>
                        <td>{batch.gateway_name || `Gateway ${batch.gateway_id}`}</td>
                        <td>
                          <span className={`badge ${
                            batch.status === 'completed' ? 'bg-success' :
                            batch.status === 'processing' ? 'bg-warning' :
                            batch.status === 'paused' ? 'bg-info' :
                            batch.status === 'failed' ? 'bg-danger' : 'bg-secondary'
                          }`}>
                            {batch.status}
                          </span>
                        </td>
                        <td>
                          <small>
                            {batch.completed_sends}/{batch.total_sends} sent
                            {batch.failed_sends > 0 && (
                              <span className="text-danger"> ({batch.failed_sends} failed)</span>
                            )}
                          </small>
                        </td>
                        <td>
                          <small>
                            {batch.created_at ? new Date(batch.created_at).toLocaleString() : '-'}
                          </small>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBatchId(batch.id);
                              setBatchStatus(null);
                              setEventLog([]);
                              setPreviousStatus(null);
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendSMSForm;
